import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('component.main.project.add');
import { OnInit, Input, Output, EventEmitter, ChangeDetectorRef as CDR } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { IProjectFormData } from '../@types';
import { INSPECTION_TYPES } from '../@constants';
import { projectService } from '../@services';

declare let wiz: any;

interface IUser {
    id: string;
    name: string;
    email: string;
    department: string;
    role: string;
    selected?: boolean;
}

interface IUploadFileInfo {
    name: string;
    size: string;
    type: string;
}

interface IProjectFormWithUploads extends IProjectFormData {
    documents: { files: IUploadFileInfo[] };
    drawing: { files: IUploadFileInfo[]; description: string };
}

const PROJECT_CATEGORY_OTHER = '기타';
const LEGACY_SHARED_CATEGORY = '전체';
const LEGACY_RESEARCH_CATEGORY = '기타(연구소)';

@Component({
    selector: 'wiz-component-main-project-add',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/component.main.project.add/view.scss */
:host {
  display: block;
}

.project-add-modal {
  display: flex;
  flex-direction: column;
  height: min(48rem, 100vh - 2rem);
  overflow: hidden;
}

.project-add-body {
  flex: 1 1 auto;
  min-height: 0;
}

.project-add-pane {
  min-height: 0;
  height: 100%;
}`],
})
export class ComponentMainProjectAddComponent implements OnInit {
    @Input() mode: string = 'create';
    @Input() projectData: any = null;
    @Output() close = new EventEmitter<any>();

    public step: number = 1;
    public isSubmitting: boolean = false;
    public data: IProjectFormWithUploads = this.createInitialData();

    public categories: string[] = ['건축', '토목', PROJECT_CATEGORY_OTHER];
    public types: string[] = INSPECTION_TYPES;
    public uploadFiles: File[] = [];
    public documentUploadFiles: File[] = [];
    public departments: string[] = ['건축', '토목', '기타'];
    public selectedDepartment: string = '';
    public users: IUser[] = [];
    public selectedParticipants: IUser[] = [];
    public userSearch: string = '';
    public projectDuration: number = 0;
    public isDocumentDragOver: boolean = false;

    private readonly allowedDocumentExtensions: string[] = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'hwp', 'hwpx', 'txt', 'zip'];

    public get isEditMode(): boolean {
        return this.mode === 'edit';
    }

    public get filteredUsers(): IUser[] {
        if (!this.userSearch.trim()) return this.users;
        const keyword = this.userSearch.toLowerCase().trim();
        return this.users.filter(user =>
            user.name.toLowerCase().includes(keyword) ||
            user.email.toLowerCase().includes(keyword)
        );
    }

    public get contractAmountNumber(): number {
        const normalized = this.normalizeCurrency(this.data.contract_amount);
        return normalized ? Number(normalized) : 0;
    }

    public get contractSupplyAmount(): number {
        if (!this.contractAmountNumber) {
            return 0;
        }
        return Math.floor(this.contractAmountNumber / 1.1);
    }

    constructor(@Inject( Service) public service: Service,@Inject( CDR)  public ref: CDR) { }

    public async ngOnInit() {
        if (this.isEditMode && this.projectData) {
            this.applyProjectData(this.projectData);
        }
    }

    private createInitialData(): IProjectFormWithUploads {
        return {
            project_no: '',
            title: '',
            category: PROJECT_CATEGORY_OTHER,
            type: '',
            type_etc: '',
            participants: '',
            start_date: '',
            end_date: '',
            contract_amount: '',
            client: '',
            supervisor_name: '',
            supervisor_phone: '',
            contract_manager_name: '',
            contract_manager_phone: '',
            documents: { files: [] },
            drawing: { files: [], description: '' }
        };
    }

    private applyProjectData(project: any) {
        const rawType = project?.type || '';
        const isKnownType = this.types.includes(rawType);

        this.data = {
            project_no: project?.project_no || '',
            title: project?.title || '',
            category: this.normalizeCategory(project?.paper_category || project?.category),
            type: isKnownType ? rawType : '기타(직접입력)',
            type_etc: isKnownType ? '' : rawType,
            participants: project?.participants || '',
            start_date: this.normalizeDate(project?.start_date),
            end_date: this.normalizeDate(project?.end_date),
            contract_amount: project?.contract_amount ? this.formatCurrency(project.contract_amount) : '',
            client: project?.client || '',
            supervisor_name: project?.supervisor_name || '',
            supervisor_phone: project?.supervisor_phone || '',
            contract_manager_name: project?.contract_manager_name || '',
            contract_manager_phone: project?.contract_manager_phone || '',
            documents: { files: [] },
            drawing: { files: [], description: '' }
        };

        this.selectedParticipants = this.buildParticipantPlaceholders(this.data.participants);
        this.calculateDuration();
    }

    private buildParticipantPlaceholders(participants: string): IUser[] {
        if (!participants?.trim()) {
            return [];
        }

        return participants
            .split(',')
            .map(name => name.trim())
            .filter(name => name)
            .map((name, index) => ({
                id: `participant-${index}-${name}`,
                name,
                email: '',
                department: '등록된 참여자',
                role: 'user',
                selected: true
            }));
    }

    private normalizeDate(value: string): string {
        return String(value || '').split(' ')[0];
    }

    private normalizeCategory(value: string): string {
        const category = String(value || '')
            .split(',')
            .map((item: string) => item.trim())
            .filter((item: string) => !!item)[0] || '';
        if (!category || category === LEGACY_SHARED_CATEGORY || category === LEGACY_RESEARCH_CATEGORY) {
            return PROJECT_CATEGORY_OTHER;
        }
        if (!this.categories.includes(category)) {
            return PROJECT_CATEGORY_OTHER;
        }
        return category;
    }

    private normalizeCurrency(value: string): string {
        return String(value || '').replace(/[^0-9]/g, '');
    }

    private getFileExtension(fileName: string): string {
        if (!fileName.includes('.')) {
            return '';
        }
        return fileName.toLowerCase().split('.').pop() || '';
    }

    private isAcceptedDocument(file: File): boolean {
        return this.allowedDocumentExtensions.includes(this.getFileExtension(file.name));
    }

    private formatUploadFileSize(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    }

    private createUploadFileInfo(file: File, fallbackType: string = 'application/octet-stream'): IUploadFileInfo {
        return {
            name: file.name,
            size: this.formatUploadFileSize(file.size),
            type: file.type || fallbackType
        };
    }

    private async appendDrawingFiles(fileList: FileList | File[]) {
        for (const file of Array.from(fileList)) {
            const isImage = file.type.startsWith('image/');
            const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            if (!isImage && !isPdf) continue;

            const exists = this.uploadFiles.some(item => item.name === file.name && item.size === file.size);
            if (exists) continue;

            this.uploadFiles.push(file);
            this.data.drawing.files.push(this.createUploadFileInfo(file, isPdf ? 'application/pdf' : 'image/*'));
        }
        await this.service.render();
    }

    private async appendDocumentFiles(fileList: FileList | File[]) {
        for (const file of Array.from(fileList)) {
            if (!this.isAcceptedDocument(file)) continue;

            const exists = this.documentUploadFiles.some(item => item.name === file.name && item.size === file.size);
            if (exists) continue;

            this.documentUploadFiles.push(file);
            this.data.documents.files.push(this.createUploadFileInfo(file));
        }
        await this.service.render();
    }

    private buildProjectPayload() {
        return {
            project_no: this.data.project_no.trim(),
            title: this.data.title,
            category: this.normalizeCategory(this.data.category),
            type: this.data.type,
            type_etc: this.data.type_etc,
            participants: this.data.participants,
            start_date: this.data.start_date,
            end_date: this.data.end_date,
            contract_amount: this.normalizeCurrency(this.data.contract_amount),
            client: this.data.client,
            supervisor_name: this.data.supervisor_name.trim(),
            supervisor_phone: this.data.supervisor_phone.trim(),
            contract_manager_name: this.data.contract_manager_name.trim(),
            contract_manager_phone: this.data.contract_manager_phone.trim(),
        };
    }

    private async uploadQueuedFiles(url: string, projectId: string, files: File[], extraData: Record<string, string> = {}) {
        const fd = new FormData();
        fd.append('project_id', projectId);
        for (const [key, value] of Object.entries(extraData)) {
            fd.append(key, value);
        }
        for (const file of files) {
            fd.append('file[]', file);
        }

        const response = await fetch(url, { method: 'POST', body: fd });

        try {
            return await response.json();
        } catch {
            return { code: response.status, data: '응답 처리 실패' };
        }
    }

    public formatCurrency(value: number | string): string {
        const numericValue = Number(String(value || '').replace(/[^0-9]/g, ''));
        if (!numericValue) {
            return '0';
        }
        return numericValue.toLocaleString('ko-KR');
    }

    public getQueuedDocumentIconClass(file: IUploadFileInfo): string {
        const extension = this.getFileExtension(file.name);
        if (extension === 'pdf') return 'fa-solid fa-file-pdf text-red-400';
        if (['doc', 'docx', 'hwp', 'hwpx'].includes(extension)) return 'fa-solid fa-file-word text-blue-400';
        if (['xls', 'xlsx'].includes(extension)) return 'fa-solid fa-file-excel text-emerald-400';
        if (['ppt', 'pptx'].includes(extension)) return 'fa-solid fa-file-powerpoint text-orange-400';
        if (extension === 'zip') return 'fa-solid fa-file-zipper text-amber-400';
        return 'fa-solid fa-file-lines text-slate-400';
    }

    public getQueuedDrawingIconClass(file: IUploadFileInfo): string {
        if (file.type === 'application/pdf') {
            return 'fa-solid fa-file-pdf text-red-400';
        }
        return 'fa-solid fa-image text-indigo-400';
    }

    public onContractAmountChange(value: string) {
        const normalized = this.normalizeCurrency(value);
        this.data.contract_amount = normalized ? this.formatCurrency(Number(normalized)) : '';
        this.ref.detectChanges();
    }

    public calculateDuration() {
        if (this.data.start_date && this.data.end_date) {
            const diff = new Date(this.data.end_date).getTime() - new Date(this.data.start_date).getTime();
            this.projectDuration = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1);
        } else {
            this.projectDuration = 0;
        }
        this.ref.detectChanges();
    }

    public getDepartmentButtonClass(dept: string): string {
        if (this.selectedDepartment !== dept) {
            return 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700';
        }
        const map: any = { '건축': 'bg-blue-600', '토목': 'bg-orange-600', '기타': 'bg-gray-600' };
        return (map[dept] || 'bg-gray-600') + ' text-white shadow-lg';
    }

    public getDepartmentCardClass(dept: string): string {
        const map: any = { '건축': 'bg-blue-50 border-blue-200', '토목': 'bg-orange-50 border-orange-200' };
        return map[dept] || 'bg-gray-50 border-gray-200';
    }

    public getDepartmentBadgeClass(dept: string): string {
        const map: any = { '건축': 'bg-blue-600', '토목': 'bg-orange-600' };
        return map[dept] || 'bg-gray-600';
    }

    public async selectDepartment(dept: string) {
        this.selectedDepartment = dept;
        this.userSearch = '';

        const { code, data } = await wiz.call('users', { department: dept });
        this.users = code === 200
            ? data.map((user: IUser) => ({ ...user, selected: this.selectedParticipants.some(participant => participant.id === user.id) }))
            : [];
        this.ref.detectChanges();
    }

    public toggleParticipant(user: IUser) {
        const index = this.selectedParticipants.findIndex(participant => participant.id === user.id);
        if (index > -1) {
            this.selectedParticipants.splice(index, 1);
            user.selected = false;
        } else {
            this.selectedParticipants.push({ ...user, selected: true });
            user.selected = true;
        }
        this.data.participants = this.selectedParticipants.map(participant => participant.name).join(', ');
        this.ref.detectChanges();
    }

    public removeParticipant(user: IUser) {
        this.selectedParticipants = this.selectedParticipants.filter(participant => participant.id !== user.id);
        const userInList = this.users.find(item => item.id === user.id);
        if (userInList) userInList.selected = false;
        this.data.participants = this.selectedParticipants.map(participant => participant.name).join(', ');
        this.ref.detectChanges();
    }

    private async alert(message: string, status: string = 'error') {
        await this.service.alert.show({
            title: status === 'success' ? '성공' : '알림',
            message,
            status,
            action: '확인',
            cancel: false
        });
    }

    public async next() {
        if (this.step === 1) {
            const validation = projectService.validate(this.data);
            if (!validation.valid) {
                return await this.alert(validation.message!);
            }
            this.step = 2;
        }
        this.ref.detectChanges();
    }

    public prev() {
        if (this.step > 1) this.step--;
        this.ref.detectChanges();
    }

    public async onFileChange(event: any) {
        const files = event.target.files;
        if (!files?.length) return;
        await this.appendDrawingFiles(files);
        event.target.value = '';
    }

    public removeFile(index: number) {
        this.data.drawing.files.splice(index, 1);
        this.uploadFiles.splice(index, 1);
        this.ref.detectChanges();
    }

    public async onDocumentFileChange(event: any) {
        const files = event.target.files;
        if (!files?.length) return;
        await this.appendDocumentFiles(files);
        event.target.value = '';
    }

    public onDocumentDragOver(event: DragEvent) {
        event.preventDefault();
        this.isDocumentDragOver = true;
        this.ref.detectChanges();
    }

    public onDocumentDragLeave(event: DragEvent) {
        event.preventDefault();
        this.isDocumentDragOver = false;
        this.ref.detectChanges();
    }

    public async onDocumentDrop(event: DragEvent) {
        event.preventDefault();
        this.isDocumentDragOver = false;
        const files = event.dataTransfer?.files;
        if (!files?.length) {
            await this.service.render();
            return;
        }
        await this.appendDocumentFiles(files);
    }

    public removeDocumentFile(index: number) {
        this.data.documents.files.splice(index, 1);
        this.documentUploadFiles.splice(index, 1);
        this.ref.detectChanges();
    }

    public async submit() {
        const validation = projectService.validate(this.data);
        if (!validation.valid) {
            return await this.alert(validation.message || '입력값을 확인해주세요.');
        }

        if (this.isSubmitting) return;
        this.isSubmitting = true;
        await this.service.loading.show();

        try {
            const payload = this.buildProjectPayload();
            const action = this.isEditMode ? 'update' : 'create';
            const requestData = this.isEditMode
                ? { id: this.projectData?.id || '', ...payload }
                : { ...payload, description: this.data.drawing.description };

            const { code, data } = await wiz.call(action, requestData);

            if (code !== 200) {
                await this.service.loading.hide();
                this.isSubmitting = false;
                await this.service.render();
                return await this.alert(data || (this.isEditMode ? '용역 수정에 실패했습니다.' : '용역 등록에 실패했습니다.'));
            }

            if (!this.isEditMode) {
                const projectId = data.id;

                if (this.documentUploadFiles.length > 0) {
                    const uploadResult = await this.uploadQueuedFiles(wiz.url('upload_documents'), projectId, this.documentUploadFiles);
                    if (uploadResult.code !== 200) {
                        console.warn('계약 관련 서류 업로드 실패:', uploadResult.data);
                    }
                }

                if (this.uploadFiles.length > 0) {
                    const uploadResult = await this.uploadQueuedFiles(wiz.url('upload'), projectId, this.uploadFiles, {
                        description: this.data.drawing.description || ''
                    });
                    if (uploadResult.code !== 200) {
                        console.warn('도면 업로드 실패:', uploadResult.data);
                    }
                }
            }

            await this.service.loading.hide();
            this.isSubmitting = false;
            await this.alert(this.isEditMode ? '용역 정보가 수정되었습니다.' : '새 용역이 등록되었습니다.', 'success');
            this.close.emit(true);
        } catch (e: any) {
            await this.service.loading.hide();
            this.isSubmitting = false;
            await this.service.render();
            await this.alert(e.message || '오류가 발생했습니다.');
        }
    }
}

export default ComponentMainProjectAddComponent;